import {useTranslation} from 'react-i18next';
import {Check, X} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import type {CardFaceState} from './cardFaceState';

const MASKED_NUMBER = '•••• •••• •••• ••••';

type Props = {
  state: CardFaceState;
  isCompact?: boolean;
};

function StatusPill({state}: {state: CardFaceState}) {
  const {t} = useTranslation();

  if (state === 'idle' || state === 'ready') return null;

  const isBusy = state === 'verify' || state === 'proc';

  return (
    <span
      className={cn(
        'absolute bottom-[0.7em] right-[0.7em] z-[2] inline-flex min-h-[2.6em] min-w-[2.6em] items-center justify-center gap-[0.5em] rounded-full px-[0.8em] text-[1.1em] font-semibold motion-safe:animate-in motion-safe:zoom-in-90',
        isBusy && 'bg-[var(--chekin-color-white)] text-[var(--chekin-color-brand-navy)]',
        state === 'ok' && 'bg-[var(--chekin-green-700)] text-[var(--chekin-color-white)]',
        state === 'fail' &&
          'bg-[var(--chekin-color-brand-red)] text-[var(--chekin-color-white)]',
      )}
    >
      {isBusy && (
        <span className="size-[1em] rounded-full border-2 border-[var(--chekin-blue-tint-info)] border-t-[var(--chekin-color-brand-blue)] motion-safe:animate-spin motion-safe:[animation-duration:1.5s]" />
      )}
      {state === 'verify' && t('pay_flow.card_verifying')}
      {state === 'proc' && t('pay_flow.card_processing')}
      {state === 'ok' && <Check className="size-[1.4em]" strokeWidth={3} />}
      {state === 'fail' && <X className="size-[1.4em]" strokeWidth={3} />}
    </span>
  );
}

function PaymentCardFace({state, isCompact}: Props) {
  const {t} = useTranslation();
  return (
    <div
      aria-hidden
      data-state={state}
      className={cn(
        'relative mx-auto aspect-[1.586] w-full transition-[max-width,font-size] duration-300',
        isCompact
          ? 'max-w-[168px] text-[8px] [@container(min-width:720px)]:max-w-none [@container(min-width:720px)]:text-[11px]'
          : 'max-w-[236px] text-[11px] [@container(min-width:720px)]:max-w-none',
      )}
    >
      <div
        className={cn(
          'absolute inset-0 flex flex-col justify-between overflow-hidden rounded-[1.3em] px-[1.6em] py-[1.5em] text-[var(--chekin-color-white)] [background:var(--chekin-gradient-duotone)]',
          'before:pointer-events-none before:absolute before:-top-[45%] before:right-[-20%] before:aspect-square before:w-[75%] before:rounded-full before:bg-white/[0.16] before:content-[""]',
          state === 'fail' && 'saturate-[0.25] brightness-[0.92]',
        )}
      >
        <div className="relative flex items-center justify-between">
          <span className="h-[2em] w-[2.7em] rounded-[0.4em] bg-[linear-gradient(135deg,var(--chekin-amber-100),var(--chekin-amber-400))]" />
          <span
            className={cn(
              'rounded-[0.4em] px-[0.6em] py-[0.15em] text-[0.95em] font-bold tracking-[0.08em]',
              state === 'ready'
                ? 'bg-white text-[var(--chekin-color-brand-navy)]'
                : 'bg-white/20',
            )}
          >
            {state === 'ready' ? (
              <Check className="size-[1.3em]" strokeWidth={3} />
            ) : (
              t('pay_flow.card_label')
            )}
          </span>
        </div>
        <div className="relative text-[1.5em] font-medium tracking-[0.08em] tabular-nums">
          {MASKED_NUMBER}
        </div>
        <div className="relative flex flex-col">
          <small className="text-[0.75em] uppercase tracking-[0.06em] opacity-75">
            {t('pay_flow.card_valid_thru')}
          </small>
          <b className="text-[1.2em] font-semibold">
            {t('pay_flow.card_expiry_placeholder')}
          </b>
        </div>
      </div>
      <StatusPill state={state} />
    </div>
  );
}

export {PaymentCardFace};
